import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, FileText, Lock, FlaskConical } from "lucide-react";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Reprocess({ s, onDone }) {
  const { accounts } = useApp();
  const [acct, setAcct] = useState("");
  const [pw, setPw] = useState("");
  const go = async () => {
    try { await api.post(`/statements/${s.id}/reprocess`, { account_id: acct || null, password: pw || null }); toast.success("Reprocessing"); onDone(); }
    catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {s.status === "needs_account" && (
        <Select value={acct} onValueChange={setAcct}>
          <SelectTrigger className="h-8 w-56" data-testid="reprocess-account-select"><SelectValue placeholder="Choose bank account" /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.bank_name} {a.masked}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {s.status === "password_required" && <Input type="password" className="h-8 w-48" placeholder="PDF password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reprocess-password-input" />}
      <Button size="sm" onClick={go} data-testid="reprocess-button">Reprocess</Button>
    </div>
  );
}

export default function Upload() {
  const { accounts, can } = useApp();
  const nav = useNavigate();
  const [stmts, setStmts] = useState([]);
  const [acct, setAcct] = useState("auto");
  const [pw, setPw] = useState("");
  const [drag, setDrag] = useState(false);
  const [uploads, setUploads] = useState([]);
  const input = useRef();
  const load = useCallback(() => api.get("/statements").then((r) => setStmts(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!stmts.some((s) => ["queued", "processing"].includes(s.status))) return;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [stmts, load]);
  const send = async (files) => {
    for (const file of files) {
      const key = file.name + Date.now();
      setUploads((u) => [...u, { key, name: file.name, pct: 0 }]);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("account_id", acct === "auto" ? "" : acct);
      fd.append("password", pw);
      try {
        await api.post("/statements/upload", fd, { onUploadProgress: (e) => setUploads((u) => u.map((x) => x.key === key ? { ...x, pct: Math.round((e.loaded / (e.total || 1)) * 100) } : x)) });
        toast.success(`${file.name} uploaded — extracting`);
      } catch (e) { toast.error(`${file.name}: ${await errMsg(e)}`); }
      setUploads((u) => u.filter((x) => x.key !== key));
    }
    setPw("");
    load();
  };
  const samples = async () => {
    try { const r = await api.post("/statements/load-samples"); toast.success(r.data.ids.length ? `${r.data.ids.length} sample statements queued` : "Samples were already loaded"); load(); }
    catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Upload Statements" sub="PDF (text or scanned), password-protected PDF, CSV or Excel. Deterministic extraction first, OCR for scanned pages, AI only for suggestions. Passwords are used once and never stored.">
        <Button variant="outline" onClick={samples} disabled={!can("admin", "accountant")} data-testid="load-samples-button"><FlaskConical className="mr-2 h-4 w-4" />Load sample statements</Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); send([...e.dataTransfer.files]); }}
          onClick={() => input.current?.click()} data-testid="upload-statement-dropzone"
          className={`grid-bg flex min-h-[260px] cursor-pointer flex-col items-start justify-end rounded-2xl border-2 border-dashed p-8 transition-[border-color,background-color] duration-200 ${drag ? "border-primary bg-primary/5" : "hover:border-primary/60"}`}>
          <UploadCloud className="mb-4 h-10 w-10 text-primary" />
          <div className="font-display text-xl font-bold">Drop bank statements here</div>
          <div className="mt-1 text-sm text-muted-foreground">or click to browse · PDF, CSV, XLSX · up to 25 MB each · Axis, ICICI, Yes, HDFC, SBI and other Indian banks</div>
          <input ref={input} type="file" multiple accept=".pdf,.csv,.xlsx,.xls" className="hidden" onChange={(e) => { send([...e.target.files]); e.target.value = ""; }} data-testid="upload-file-input" />
          {uploads.map((u) => <div key={u.key} className="mt-3 w-full max-w-md"><div className="text-xs">{u.name}</div><Progress value={u.pct} className="h-1.5" /></div>)}
        </div>
        <div className="panel space-y-4 p-5">
          <div>
            <div className="label-xs mb-2">Bank account</div>
            <Select value={acct} onValueChange={setAcct}>
              <SelectTrigger data-testid="upload-account-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="auto">Auto-detect from statement</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.bank_name} {a.masked}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-xs mb-2 flex items-center gap-1"><Lock className="h-3 w-3" />PDF password (if protected)</div>
            <Input type="password" autoComplete="off" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Used once, not stored" data-testid="pdf-password-input" />
          </div>
          <div className="text-xs leading-relaxed text-muted-foreground">Files are encrypted at rest. Identical files are rejected; overlapping periods are checked row-by-row for duplicates.</div>
        </div>
      </div>
      <h3 className="mb-3 mt-10 font-semibold">Statement history</h3>
      <div className="space-y-3">
        {stmts.map((s) => (
          <div key={s.id} className="panel p-4 fade-up" data-testid="statement-row">
            <div className="flex flex-wrap items-center gap-4">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{s.filename}</div>
                <div className="text-xs text-muted-foreground">{s.detected_bank || accounts.find((a) => a.id === s.account_id)?.bank_name || "Bank not detected"} · {s.period_start ? `${fmtDate(s.period_start)} – ${fmtDate(s.period_end)}` : "period —"} · {s.row_count ?? 0} rows · {s.extraction_method || "…"} · uploaded {s.uploaded_at.slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="num hidden text-right text-xs md:block">Dr {inr(s.total_debits)}<br />Cr {inr(s.total_credits)}</div>
              <Pill s={s.status} testid="statement-status" />
              {["verified", "failed"].includes(s.status) && <Button size="sm" variant="outline" onClick={() => nav(`/statements/${s.id}`)} data-testid="open-statement-button">Verify side-by-side</Button>}
            </div>
            {["queued", "processing"].includes(s.status) && <div className="mt-3"><div className="mb-1 text-xs text-muted-foreground">{s.stage}</div><Progress value={s.progress} className="h-1.5" /></div>}
            {["needs_account", "password_required", "error"].includes(s.status) && <><div className="mt-2 text-xs text-amber-600 dark:text-amber-400">{s.stage}{s.detected_account ? ` · statement shows ${s.detected_account}` : ""}</div><Reprocess s={s} onDone={load} /></>}
          </div>
        ))}
        {stmts.length === 0 && <div className="text-sm text-muted-foreground">No statements uploaded yet.</div>}
      </div>
    </div>
  );
}
